import { Badge } from "@/components/ui/badge";
import { MapPin, Calendar, Briefcase, Clock, Globe } from "lucide-react";
import { format } from "date-fns";

interface OpportunityModalContentProps {
  opportunity: {
    title: string;
    description: string;
    location?: string | null;
    deadline?: string | null;
    compensation?: string | null;
    experience_level?: string | null;
    remote_allowed?: boolean;
    requirements?: string | null;
    skills_required?: string[];
    languages?: string[];
    physical_requirements?: string | null;
  };
}

export const OpportunityModalContent = ({ opportunity }: OpportunityModalContentProps) => {
  return (
    <div>
      <h2 className="text-xl font-semibold mb-2">{opportunity.title}</h2>
      <div className="flex flex-wrap gap-3 mb-4">
        {opportunity.location && (
          <Badge variant="secondary" className="flex items-center gap-1">
            <MapPin className="h-3 w-3" />
            {opportunity.location}
          </Badge>
        )}
        {opportunity.deadline && (
          <Badge variant="secondary" className="flex items-center gap-1">
            <Calendar className="h-3 w-3" />
            Deadline: {format(new Date(opportunity.deadline), 'MMM d, yyyy')}
          </Badge>
        )}
        {opportunity.compensation && (
          <Badge variant="secondary" className="flex items-center gap-1">
            <Briefcase className="h-3 w-3" />
            {opportunity.compensation}
          </Badge>
        )}
        {opportunity.experience_level && (
          <Badge variant="secondary" className="flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {opportunity.experience_level}
          </Badge>
        )}
        {opportunity.remote_allowed && (
          <Badge variant="secondary" className="flex items-center gap-1">
            <Globe className="h-3 w-3" />
            Remote Available
          </Badge>
        )}
      </div>
      
      <div className="space-y-4">
        <div>
          <h3 className="font-semibold mb-2">Description</h3>
          <p className="text-sm whitespace-pre-line">{opportunity.description}</p>
        </div>
        
        {opportunity.requirements && (
          <div>
            <h3 className="font-semibold mb-2">Requirements</h3>
            <p className="text-sm text-muted-foreground">{opportunity.requirements}</p>
          </div>
        )}

        {opportunity.skills_required && opportunity.skills_required.length > 0 && (
          <div>
            <h3 className="font-semibold mb-2">Required Skills</h3>
            <div className="flex flex-wrap gap-2">
              {opportunity.skills_required.map((skill, index) => (
                <Badge key={index} variant="outline">
                  {skill}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {opportunity.languages && opportunity.languages.length > 0 && (
          <div>
            <h3 className="font-semibold mb-2">Languages</h3>
            <div className="flex flex-wrap gap-2">
              {opportunity.languages.map((language, index) => (
                <Badge key={index} variant="outline">
                  {language}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {opportunity.physical_requirements && (
          <div>
            <h3 className="font-semibold mb-2">Physical Requirements</h3>
            <p className="text-sm text-muted-foreground">{opportunity.physical_requirements}</p>
          </div>
        )}
      </div>
    </div>
  );
};